/**
 * Regole automatiche sulla dicitura.
 *
 * Stanno in una pagina loro e non dentro la riconciliazione perché sono due
 * lavori diversi: la riconciliazione si fa quando arrivano i movimenti, le
 * regole si scrivono una volta e poi lavorano da sole. Tenerle insieme
 * riempiva una schermata che serve a decidere in fretta.
 */
import { useState } from 'react';
import { api, type Categoria } from '../api';
import {
  Campo,
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  Scheda,
  Vuoto,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
} from '../componenti/base';
import { formattaData, formattaEuro, plurale } from '../formato';
import { useSessione } from '../main';

export interface Regola {
  id: string;
  dicitura: string;
  verso: 'ENTRATA' | 'USCITA';
  categoriaId: string;
  attiva: boolean;
  categoria: { id: string; nome: string };
  proposte: number;
}

export function Regole() {
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  const regole = useCarico(() => api.get<Regola[]>('/api/regole'), []);

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Regole automatiche</h1>
        <p className="text-sm text-inchiostro-500">
          I bonifici ricorrenti ripetono la stessa causale ogni mese. Una regola la riconosce e propone la voce, così non
          la assegni più a mano. Restano <strong>proposte</strong>: un movimento deciso da una persona non viene mai
          toccato.
        </p>
      </header>

      <PannelloRegole
        regole={regole.dati ?? []}
        caricamento={regole.caricamento}
        categorie={categorie.dati ?? []}
        onCambiate={() => {
          regole.ricarica();
        }}
      />
    </div>
  );
}

function PannelloRegole({
  regole,
  caricamento,
  categorie,
  onCambiate,
}: {
  regole: Regola[];
  caricamento: boolean;
  categorie: Categoria[];
  onCambiate: () => void;
}) {
  const avvisa = useAvvisi();
  const [nuova, setNuova] = useState(false);
  const [inModifica, setInModifica] = useState<Regola | null>(null);

  const elimina = async (r: Regola) => {
    if (!confirm(`Eliminare la regola «${r.dicitura}»? Le ${r.proposte} proposte che ne derivano spariranno; le scelte fatte a mano restano.`)) return;
    try {
      await api.del(`/api/regole/${r.id}`);
      avvisa('Regola eliminata: le proposte che ne derivavano sono sparite con lei.');
      onCambiate();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  return (
    <>
      <Scheda
        titolo="Regole sulla dicitura"
        azioni={
          <button className="bottone-secondario" onClick={() => setNuova(true)}>
            Nuova regola
          </button>
        }
        senzaPadding
      >
        {/* «Nessuna regola» mentre si sta ancora caricando afferma una cosa
            falsa: finché non si sa, si dice che non si sa. */}
        {caricamento ? (
          <Caricamento />
        ) : regole.length === 0 ? (
          <Vuoto
            titolo="Nessuna regola"
            testo="Si creano da qui, oppure dal selettore della voce di un movimento — che è il momento in cui si sa qual è la voce giusta."
          />
        ) : (
          <table className="dati">
            <thead>
              <tr>
                <th>Dicitura riconosciuta</th>
                <th>Verso</th>
                <th>Voce proposta</th>
                <th className="text-right">Movimenti</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {regole.map((r) => (
                <tr key={r.id} className={r.attiva ? '' : 'opacity-50'}>
                  <td className="font-mono text-xs">{r.dicitura}</td>
                  <td>
                    <Etichetta
                      testo={r.verso === 'ENTRATA' ? 'entrate' : 'uscite'}
                      titolo="Una regola vale per un verso solo: una su «BONIFICO» che prendesse anche gli accrediti farebbe più danni che altro."
                    />
                  </td>
                  <td>{r.categoria.nome}</td>
                  <td className="num text-right">
                    {r.proposte === 0 ? (
                      <span className="text-attenzione" title="Questa regola non sta proponendo niente: forse la dicitura non compare in nessuna causale.">
                        nessuno
                      </span>
                    ) : (
                      r.proposte
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        className="text-xs underline underline-offset-2"
                        title="Cambia la parte di causale o la voce. Restringere la dicitura è il modo di far valere una regola per più casi simili."
                        onClick={() => setInModifica(r)}
                      >
                        Modifica
                      </button>
                      <button className="text-xs text-allerta underline underline-offset-2" onClick={() => elimina(r)}>
                        Elimina
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Scheda>

      {nuova && (
        <DialogoRegola
          categorie={categorie}
          dicituraIniziale=""
          versoIniziale="USCITA"
          onChiudi={() => setNuova(false)}
          onCreata={() => {
            setNuova(false);
            onCambiate();
          }}
        />
      )}

      {inModifica && (
        <DialogoRegola
          categorie={categorie}
          regolaEsistente={inModifica}
          dicituraIniziale={inModifica.dicitura}
          versoIniziale={inModifica.verso}
          categoriaIniziale={inModifica.categoria.id}
          introduzione="Accorciare la dicitura allarga la regola: «SEPA B2» prende sia i B2B sia i B2C, senza doverne fare due. Guarda l’anteprima prima di salvare."
          onChiudi={() => setInModifica(null)}
          onCreata={() => {
            setInModifica(null);
            onCambiate();
          }}
        />
      )}
    </>
  );
}

interface AnteprimaRegola {
  prenderebbe: { id: string; data: string; importoCent: number; dicitura: string }[];
  giaDecisi: { id: string; data: string; importoCent: number; dicitura: string; categoria: string | null }[];
}

type Decorrenza = 'SEMPRE' | 'DA_QUI';

export function DialogoRegola({
  categorie,
  dicituraIniziale,
  versoIniziale,
  categoriaIniziale,
  dataMovimento,
  titolo,
  introduzione,
  regolaEsistente,
  onChiudi,
  onCreata,
}: {
  categorie: Categoria[];
  /**
   * La regola da modificare. Quando c'è, il dialogo aggiorna invece di creare:
   * il modulo è lo stesso perché i campi sono gli stessi, e due dialoghi quasi
   * identici sono due posti dove correggere ogni futura modifica.
   */
  regolaEsistente?: { id: string; dicitura: string };
  dicituraIniziale: string;
  versoIniziale: 'ENTRATA' | 'USCITA';
  /** Voce già scelta, quando la regola nasce da una categorizzazione appena fatta. */
  categoriaIniziale?: string;
  /** Data del movimento da cui è nata: è il «da qui in avanti». */
  dataMovimento?: string;
  titolo?: string;
  introduzione?: string;
  onChiudi: () => void;
  onCreata: () => void;
}) {
  const avvisa = useAvvisi();
  const { organizzazione } = useSessione();
  const { piatto } = useAlberoCategorie(categorie);
  const [dicitura, setDicitura] = useState(dicituraIniziale);
  const [verso, setVerso] = useState(versoIniziale);
  // Il menu della voce non ha un valore preselezionato: partire su una voce
  // mentre si legge il nome di un'altra è una trappola silenziosa.
  const [categoriaId, setCategoriaId] = useState(categoriaIniziale ?? '');
  const [decorrenza, setDecorrenza] = useState<Decorrenza>(dataMovimento ? 'DA_QUI' : 'SEMPRE');
  const validaDa = decorrenza === 'DA_QUI' ? (dataMovimento ?? null) : null;
  const [anteprima, setAnteprima] = useState<AnteprimaRegola | null>(null);
  const [attesa, setAttesa] = useState(false);

  const guarda = async () => {
    if (dicitura.trim().length < 2) return;
    try {
      setAnteprima(
        await api.post<AnteprimaRegola>('/api/regole/anteprima', { dicitura: dicitura.trim(), verso, validaDa }),
      );
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Anteprima non riuscita.', 'errore');
    }
  };

  const crea = async () => {
    setAttesa(true);
    try {
      // Modificando non si tocca «da quando vale»: quella decisione è stata
      // presa quando la regola è nata, e riscriverla qui rimetterebbe in
      // discussione mesi già sistemati senza che nessuno l'abbia chiesto.
      const r = regolaEsistente
        ? await api.put<{ esito: { proposte: number } }>(`/api/regole/${regolaEsistente.id}`, {
            dicitura: dicitura.trim(),
            categoriaId,
          })
        : await api.post<{ esito: { proposte: number } }>('/api/regole', {
            dicitura: dicitura.trim(),
            verso,
            categoriaId,
            validaDa,
          });
      avvisa(
        `${regolaEsistente ? 'Regola aggiornata' : 'Regola creata'}: ${plurale(r.esito.proposte, 'movimento ha', 'movimenti hanno')} ricevuto una proposta.`,
      );
      onCreata();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const assegnabili = piatto.filter((p) => p.categoria.verso === verso && !p.categoria.sistema);

  return (
    <Dialogo
      aperto
      titolo={titolo ?? (regolaEsistente ? 'Modifica la regola' : 'Nuova regola sulla dicitura')}
      onChiudi={onChiudi}
      larghezza="max-w-2xl"
    >
      <div className="space-y-4">
        {introduzione && <p className="text-sm text-inchiostro-600">{introduzione}</p>}

        {dataMovimento && (
          <Campo
            etichetta="Da quando vale"
            aiuto="I mesi già sistemati a mano sono lavoro fatto: «da qui in avanti» non li rimette in discussione."
          >
            <div className="space-y-1.5">
              {(
                [
                  ['DA_QUI', `Da questo movimento in poi (dal ${formattaData(dataMovimento, organizzazione.formatoData)})`],
                  ['SEMPRE', 'Sempre, anche sui movimenti già passati'],
                ] as const
              ).map(([valore, testo]) => (
                <label key={valore} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={decorrenza === valore}
                    onChange={() => {
                      setDecorrenza(valore);
                      setAnteprima(null);
                    }}
                  />
                  {testo}
                </label>
              ))}
            </div>
          </Campo>
        )}
        <Campo
          etichetta="Su quali movimenti"
          aiuto="Una regola vale per un verso solo: gli incassi ricorrenti (SEPA, RID a tuo favore) vogliono una regola sulle entrate."
        >
          <select
            className="campo w-56"
            value={verso}
            // In modifica il verso è bloccato: ribaltarlo su una regola già in
            // uso sposterebbe di colpo tutte le proposte che ne dipendono, e
            // non è quello che si va a fare quando si corregge una dicitura.
            // Per cambiare verso si elimina e si rifà, che è anche più onesto.
            disabled={!!regolaEsistente}
            onChange={(e) => {
              setVerso(e.target.value as 'ENTRATA' | 'USCITA');
              // Le voci assegnabili dipendono dal verso: tenerne una del verso
              // precedente farebbe rifiutare la regola dal server.
              setCategoriaId('');
              setAnteprima(null);
            }}
          >
            <option value="USCITA">Uscite (pagamenti)</option>
            <option value="ENTRATA">Entrate (incassi)</option>
          </select>
        </Campo>

        <Campo
          etichetta={`Parte stabile della causale (${verso === 'ENTRATA' ? 'entrate' : 'uscite'})`}
          aiuto="Quale sia la parte che si ripete ogni mese lo riconosce una persona: il suggerimento va corretto."
        >
          <input
            className="campo font-mono text-sm"
            value={dicitura}
            onChange={(e) => {
              setDicitura(e.target.value);
              setAnteprima(null);
            }}
            onBlur={guarda}
            placeholder="Es. CANONE LOCAZIONE CAPANNONE"
          />
        </Campo>

        <Campo etichetta="Voce da proporre">
          <select className="campo" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
            <option value="">Scegli…</option>
            {assegnabili.map(({ categoria, livello }) => (
              <option key={categoria.id} value={categoria.id}>
                {' '.repeat(livello * 3)}
                {categoria.nome}
              </option>
            ))}
          </select>
        </Campo>

        <div className="flex items-center gap-2">
          <button className="bottone-secondario" onClick={guarda} disabled={dicitura.trim().length < 2}>
            Guarda cosa prenderebbe
          </button>
          <span className="text-xs text-inchiostro-500">È la protezione contro la regola troppo larga.</span>
        </div>

        {anteprima && (
          <div className="space-y-3">
            <div className="rounded-md border border-inchiostro-200 p-3">
              <p className="text-sm font-medium">
                Prenderebbe {plurale(anteprima.prenderebbe.length, 'movimento', 'movimenti')}
              </p>
              {anteprima.prenderebbe.length === 0 ? (
                // Distinguere i due «zero»: una regola che non prende niente
                // perché la dicitura è sbagliata è un errore da correggere; una
                // che non prende niente perché guarda solo in avanti è
                // esattamente ciò che è stato chiesto.
                anteprima.giaDecisi.length > 0 || decorrenza === 'DA_QUI' ? (
                  <p className="mt-1 text-xs text-inchiostro-500">
                    Nessun movimento passato da sistemare
                    {anteprima.giaDecisi.length > 0 && ' (quelli che ci sono li hai già decisi a mano, e restano com’erano)'}
                    : la regola varrà sui prossimi che arriveranno.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-attenzione">
                    Nessun movimento contiene questa dicitura: la regola resterebbe senza effetto.
                  </p>
                )
              ) : (
                <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs">
                  {anteprima.prenderebbe.slice(0, 20).map((m) => (
                    <li key={m.id} className="flex justify-between gap-3">
                      <span className="truncate">
                        <span className="num text-inchiostro-500">{formattaData(m.data, organizzazione.formatoData)}</span>{' '}
                        {m.dicitura}
                      </span>
                      <span className="num shrink-0">{formattaEuro(m.importoCent)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {anteprima.giaDecisi.length > 0 && (
              <div className="rounded-md border border-saldo/30 bg-saldo-chiaro p-3 text-sm text-saldo">
                <p className="font-medium">
                  {plurale(anteprima.giaDecisi.length, 'movimento è', 'movimenti sono')} già stati decisi a mano e
                  resteranno com’erano.
                </p>
                <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-xs">
                  {anteprima.giaDecisi.slice(0, 10).map((m) => (
                    <li key={m.id} className="truncate">
                      {m.dicitura} → <strong>{m.categoria ?? 'nessuna voce'}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
          {/* Quando la regola nasce da una categorizzazione, rinunciare non è
              «annullare»: la voce è già stata assegnata a quel movimento e
              resta. Dirlo evita di far credere che si stia disfacendo tutto. */}
          <button className="bottone-secondario" onClick={onChiudi}>
            {dataMovimento ? 'No, solo questo movimento' : 'Annulla'}
          </button>
          <button className="bottone-primario" onClick={crea} disabled={attesa || !categoriaId || dicitura.trim().length < 2}>
            {regolaEsistente ? 'Salva la regola' : 'Crea la regola'}
          </button>
        </div>
      </div>
    </Dialogo>
  );
}